/**
 * The earcons of voice capture: the wake cue once the microphone listens, the
 * wind-down cue once it is let go. Live mode has its own off cue; its start
 * tone lives in `live-voice-start-cue`.
 *
 * `stop_dictation` resolves as soon as the engine has the command; the macOS
 * speech sidecar then keeps capturing for its 250 ms tail
 * (`STOP_TAIL_CAPTURE_GRACE_S`) before it stops the audio engine. Releasing the
 * microphone switches the output route (Bluetooth HFP back to A2DP), which
 * swallows a cue already playing — so the stop cue waits for both.
 */
import {
  playDictationWakeSound,
  playDictationWindDownSound,
  playLiveVoiceOffSound,
} from "@/lib/notification-sound";

/** The sidecar's tail capture plus room for the output route to switch. */
export const VOICE_STOP_CUE_DELAY_MS = 400;

/** Bumped by every stop, start and cancel: only the latest stop's cue plays. */
let generation = 0;

/**
 * A user stop begins. Call the returned function once the stop command has
 * been accepted: it plays the cue, unless the recording was cancelled,
 * restarted or stopped again in the meantime.
 */
export function beginVoiceStopCue(capture: "dictation" | "live"): () => void {
  const stop = ++generation;
  const play =
    capture === "live" ? playLiveVoiceOffSound : playDictationWindDownSound;
  return () => {
    setTimeout(() => {
      if (stop === generation) play();
    }, VOICE_STOP_CUE_DELAY_MS);
  };
}

/** A cancel or a new start: a stop cue not yet played stays silent. */
export function dropVoiceStopCue(): void {
  generation += 1;
}

/** Dictation has started: the wake cue replaces a stop cue still waiting. */
export function playDictationStartCue(): void {
  dropVoiceStopCue();
  playDictationWakeSound();
}
